import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import { Calendar, Plus, FileText } from "@phosphor-icons/react";
import { CuteLoader } from "@/components/CuteLoader";

export default function AdminPayrollPage() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      const { data } = await api.get("/payroll-periods");
      setPeriods(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="border-b border-border bg-card px-4 py-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Payroll</h1>
        <p className="text-sm text-muted-foreground mt-1">Pay periods and imports</p>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => setShowNewPeriodModal(true)}
          >
            <Plus className="h-6 w-6" />
            <span className="text-xs">New Period</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => setShowImportModal(true)}
          >
            <FileText className="h-6 w-6" />
            <span className="text-xs">Import Data</span>
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Payroll Periods ({periods.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {periods.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No payroll periods found
              </div>
            ) : (
              <div className="divide-y">
                {periods.map((period) => (
                  <div key={period._id} className="p-4 hover:bg-muted">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-foreground">
                          {formatDate(period.start_date)} - {formatDate(period.end_date)}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Status: <span className="capitalize">{period.status}</span>
                        </p>
                      </div>
                      <div className="text-muted-foreground">›</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Period Modal */}
      {showNewPeriodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Create New Payroll Period</h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                This feature is under construction. Please use the backend scripts to create periods.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowNewPeriodModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Data Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Import Data</h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Import UI is under construction. Please use the backend import scripts for now.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
